import type { HistoricalOverlayId, OverlayStates } from './historical-overlays';

/**
 * Year-to-overlay mapping. Pure function — no React, no store.
 *
 * Each historical overlay has a peak year (when its source map most
 * accurately represents Flanders) and a triangular falloff around that peak.
 * The result is a smooth cross-fade as the year slider moves.
 *
 * | Overlay              | Peak | Plateau ½-width | Total fade-out distance |
 * |----------------------|------|-----------------|-------------------------|
 * | Pourbus              | 1571 | 30              | 50                      |
 * | Fricx                | 1712 | 25              | 50                      |
 * | Ferraris             | 1777 | 25              | 50                      |
 * | Atlas der Buurtwegen | 1841 | 20              | 30                      |
 * | Vandermaelen         | 1850 | 20              | 40                      |
 * | Popp                 | 1860 | 25              | 45                      |
 *
 * Inside the plateau (|year - peak| <= half-width) opacity is the visible
 * default (0.85). Outside the plateau, opacity falls linearly to 0 over the
 * next `fadeOutDistance` years. Past that distance the overlay is invisible.
 * At 2026 all overlays are 0 (modern map only).
 *
 * Modern base is the underlying tile style — always rendered, but obscured
 * when historical overlays are highly opaque.
 */

export const YEAR_MIN = 1500;
export const YEAR_MAX = 2026;

interface OverlayDescriptor {
  peak: number;
  plateauHalfWidth: number;
  fadeOutDistance: number;
}

const VISIBLE_OPACITY = 0.85;

const OVERLAY_DESCRIPTORS: Record<HistoricalOverlayId, OverlayDescriptor> = {
  pourbus: { peak: 1571, plateauHalfWidth: 30, fadeOutDistance: 50 },
  fricx: { peak: 1712, plateauHalfWidth: 25, fadeOutDistance: 50 },
  ferraris: { peak: 1777, plateauHalfWidth: 25, fadeOutDistance: 50 },
  buurtwegen: { peak: 1841, plateauHalfWidth: 20, fadeOutDistance: 30 },
  vandermaelen: { peak: 1850, plateauHalfWidth: 20, fadeOutDistance: 40 },
  popp: { peak: 1860, plateauHalfWidth: 25, fadeOutDistance: 45 },
};

export type OverlayOpacityForYear = Record<HistoricalOverlayId, number>;

/** Compute each overlay's opacity (0..1) for the given year. */
export function overlayOpacitiesForYear(year: number): OverlayOpacityForYear {
  return {
    pourbus: opacityForOverlay(year, OVERLAY_DESCRIPTORS.pourbus),
    fricx: opacityForOverlay(year, OVERLAY_DESCRIPTORS.fricx),
    ferraris: opacityForOverlay(year, OVERLAY_DESCRIPTORS.ferraris),
    buurtwegen: opacityForOverlay(year, OVERLAY_DESCRIPTORS.buurtwegen),
    vandermaelen: opacityForOverlay(year, OVERLAY_DESCRIPTORS.vandermaelen),
    popp: opacityForOverlay(year, OVERLAY_DESCRIPTORS.popp),
  };
}

function opacityForOverlay(year: number, d: OverlayDescriptor): number {
  const distance = Math.abs(year - d.peak);
  if (distance <= d.plateauHalfWidth) return VISIBLE_OPACITY;
  const fadeStart = d.plateauHalfWidth;
  const fadeEnd = d.plateauHalfWidth + d.fadeOutDistance;
  if (distance >= fadeEnd) return 0;
  const fadeProgress = (distance - fadeStart) / d.fadeOutDistance;
  return Math.max(0, VISIBLE_OPACITY * (1 - fadeProgress));
}

/**
 * Convert a year into the full OverlayStates shape consumed by the store.
 * `visible: true` for any non-zero opacity so the layer renders.
 */
export function overlayStatesForYear(year: number): OverlayStates {
  const opacities = overlayOpacitiesForYear(year);
  return {
    pourbus: { visible: opacities.pourbus > 0, opacity: opacities.pourbus },
    fricx: { visible: opacities.fricx > 0, opacity: opacities.fricx },
    ferraris: { visible: opacities.ferraris > 0, opacity: opacities.ferraris },
    buurtwegen: { visible: opacities.buurtwegen > 0, opacity: opacities.buurtwegen },
    vandermaelen: { visible: opacities.vandermaelen > 0, opacity: opacities.vandermaelen },
    popp: { visible: opacities.popp > 0, opacity: opacities.popp },
  };
}

/** Clamp + round a year value (e.g. from a slider input) to the valid range. */
export function clampYear(value: number): number {
  if (!Number.isFinite(value)) return YEAR_MAX;
  return Math.min(YEAR_MAX, Math.max(YEAR_MIN, Math.round(value)));
}
